import type {
    CompRef,
    IdMap,
    ImageGenerationServerlessResponseWithIdMapAndOldUrlMap,
    OldUrlMap,
    Pointer,
    CompRefWithHint
} from '@wix/document-services-types'

import {
    getNextIdForPrefix,
    getSectionNameByPointer,
    ignoredTemplateOutlineTypes,
    type PointerPredicate,
    reportBIError
} from './aiExtensionContent'
import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {StructureExtensionAPI} from '../structure'
import type {ComponentDefinitionExtensionAPI} from '../componentDefinition'
import {fetchImageUrlsForBuilder} from './aiExtensionUtils'
import {aiImageInjectionValidationErrorMessage, aiImageInjectionValidationErrorType} from './constants'
import {DATA_TYPES} from '../../constants/constants'
import {predicateBuilderComp} from './aiBuilderUtils'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import type {ViewerExtensionAPI} from '../viewer/viewerExtension'
import {injectImages, processAIImageError, processGeneratedImages, validateResult} from './images/utils'
import _ from 'lodash'

export interface BuilderImageOutlineDataWithIdMapAndOldUrlMap {
    outline: BuilderImageOutlineData
    idMap: IdMap
    oldUrlMap: OldUrlMap
}

export type ImagePredicate = (pointer: Pointer) => boolean

interface BuilderImageSectionData {
    images: {id: string; width: string; height: string}[]
}

interface BuilderTextSectionData {
    [componentName: string]: Record<string, string>
}

type SectionData = BuilderImageSectionData & BuilderTextSectionData

export interface BuilderImageOutlineData {
    [section: string]: SectionData
}

export const getBuilderImageOutlineData = (
    createExtArgs: CreateExtArgs,
    shouldAddImagePredicate: ImagePredicate,
    pagePointer: Pointer
): BuilderImageOutlineDataWithIdMapAndOldUrlMap => {
    const {pointers} = createExtArgs
    const {components} = createExtArgs.extensionAPI as StructureExtensionAPI
    const {componentDefinition} = createExtArgs.extensionAPI as ComponentDefinitionExtensionAPI
    const {manifest} = createExtArgs.extensionAPI as ManifestExtensionAPI
    const {innerElements} = createExtArgs.extensionAPI as InnerElementsExtensionAPI
    const {viewer} = createExtArgs.extensionAPI as ViewerExtensionAPI

    const predicateBuilder = (pointer: Pointer): boolean => {
        const componentType = components.getComponentType(pointer)
        return predicateBuilderComp(createExtArgs.extensionAPI, componentType)
    }

    const getContentRecursively = (
        rootPointer: Pointer,
        shouldAddImage: ImagePredicate,
        idMap: IdMap,
        oldUrlMap: OldUrlMap,
        typeCounter: Record<string, number>,
        sectionName: string | undefined,
        predicate: PointerPredicate,
        outline: any
    ): void => {
        const type = components.getComponentType(rootPointer)
        const toAdd = shouldAddImage(rootPointer)

        if (ignoredTemplateOutlineTypes.has(type)) {
            return
        }

        if (componentDefinition.isContainer(type)) {
            if (componentDefinition.isSection(type)) {
                const sectionNameByPointer = getSectionNameByPointer(
                    createExtArgs.extensionAPI,
                    rootPointer,
                    DATA_TYPES.anchors
                )
                sectionName = getNextIdForPrefix(sectionNameByPointer, typeCounter)
            }
            const children = pointers.structure.getChildren(rootPointer)
            for (const childPointer of children) {
                getContentRecursively(
                    childPointer,
                    shouldAddImage,
                    idMap,
                    oldUrlMap,
                    typeCounter,
                    sectionName,
                    predicate,
                    outline
                )
            }
        }
        if (!sectionName) return
        if (predicate(rootPointer)) {
            outline[sectionName] ??= {
                images: []
            }
            const textContextProps = manifest.getData(rootPointer, true, [{prop: 'text'}])
            textContextProps.forEach(context => {
                const {value: data} = context
                const mappedId = getNextIdForPrefix(context.type, typeCounter)
                return (outline[sectionName][mappedId] = data)
            })

            if (toAdd) {
                const imageContextProps = manifest.getData(rootPointer, true, [{prop: 'image'}])
                imageContextProps.forEach(context => {
                    const {prop, path, value: data} = context
                    const mappedId = getNextIdForPrefix(context.type, typeCounter)
                    const measurements =
                        path !== undefined
                            ? innerElements.getInnerElementMeasurements(path)
                            : viewer.getComponentMeasurements(rootPointer as CompRef)
                    idMap[mappedId] = {pointer: rootPointer, prop, path}
                    oldUrlMap[mappedId] = data?.uri
                    const {width, height} = measurements
                    outline[sectionName].images.push({id: mappedId, width, height})
                })
            }
        }
    }

    const outline: BuilderImageOutlineData = {}
    const typeCounter: Record<string, number> = {}
    const idMap = {}
    const oldUrlMap = {}
    getContentRecursively(
        pagePointer,
        shouldAddImagePredicate,
        idMap,
        oldUrlMap,
        typeCounter,
        undefined,
        predicateBuilder,
        outline
    )
    const outlineOfSectionsWithImages = Object.fromEntries(
        Object.entries(outline).filter(([, val]) => val.images.length > 0)
    )
    return {outline: outlineOfSectionsWithImages, idMap, oldUrlMap}
}

export const getBuilderImageOutlineDataForSG = (
    createExtArgs: CreateExtArgs,
    pointers: Pointer[]
): BuilderImageOutlineDataWithIdMapAndOldUrlMap => {
    const pagePointer = createExtArgs.pointers.structure.getPageOfComponent(pointers[0])
    const shouldAddImageInSG = (targetPointers: Pointer[]): ImagePredicate => {
        let toAdd = false
        return (pointer: Pointer) => {
            toAdd = toAdd || targetPointers.some(targetPointer => pointer.id === targetPointer.id)
            return toAdd
        }
    }
    return getBuilderImageOutlineData(createExtArgs, shouldAddImageInSG(pointers), pagePointer)
}

export const getBuilderImageOutlineDataForInjection = (
    createExtArgs: CreateExtArgs,
    pointers: Pointer[]
): BuilderImageOutlineDataWithIdMapAndOldUrlMap => {
    const pagePointer = createExtArgs.pointers.structure.getPageOfComponent(pointers[0])
    const shouldAddImageInInjection =
        (targetPointers: Pointer[]): ImagePredicate =>
        (pointer: Pointer) =>
            targetPointers.some(targetPointer => pointer.id === targetPointer.id)
    return getBuilderImageOutlineData(createExtArgs, shouldAddImageInInjection(pointers), pagePointer)
}

export const getImageInjectionForBuilder = async (
    environmentContext: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    compRefsWithHints: CompRefWithHint[],
    siteType: string,
    businessName: string,
    siteDescription: string,
    photoTheme: string | undefined,
    isSG: boolean
): Promise<ImageGenerationServerlessResponseWithIdMapAndOldUrlMap> => {
    const {serverFacade} = environmentContext
    const {coreConfig} = createExtArgs
    const {logger} = coreConfig

    const targetPointers: Pointer[] = compRefsWithHints.map(({compRef}) => compRef)

    const {outline, idMap, oldUrlMap} = isSG
        ? getBuilderImageOutlineDataForSG(createExtArgs, targetPointers)
        : getBuilderImageOutlineDataForInjection(createExtArgs, targetPointers)
    const msid = createExtArgs.dal.get(createExtArgs.pointers.rendererModel.getMetaSiteId())

    const compIdToHint = _.fromPairs(compRefsWithHints.map(({compRef, hint}) => [compRef.id, hint]))
    const imagesWithHints = _.mapValues(idMap, value => compIdToHint[value.pointer.id])

    try {
        const res = await fetchImageUrlsForBuilder(
            serverFacade,
            outline,
            imagesWithHints,
            siteType,
            businessName,
            siteDescription,
            photoTheme,
            msid,
            isSG
        )
        const imageUrls: Record<string, string> = JSON.parse(res)
        const validation = validateResult(imageUrls, idMap)
        if (!validation.isValid)
            reportBIError(
                logger,
                aiImageInjectionValidationErrorType,
                aiImageInjectionValidationErrorMessage,
                imageUrls,
                validation
            )
        injectImages(createExtArgs, {idMap, data: imageUrls})
        if (!isSG) {
            await processGeneratedImages(createExtArgs, serverFacade, imageUrls, idMap, oldUrlMap, logger)
        }
        return {idMap, data: imageUrls, oldUrlMap}
    } catch (e: any) {
        throw processAIImageError(e, logger)
    }
}
